import PropTypes from 'prop-types';
import {
    Container,
    Row,
    Col,
    Form,
    InputGroup,
    FloatingLabel,
    Button,
} from 'react-bootstrap';
import React, { useRef, useState } from 'react';

import SEO from '@/components/common/SEO';
import publicAxios from '@/shared/services/publicAxios';
import userIcon from '@/assets/img/icons/svg/userIcon.svg';
import phoneIcon from '@/assets/img/icons/svg/phoneIcon.svg';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';
import Captcha from '@/components/common/Captcha';

import './ContactUs.css';

export default function ContactUs({ inModal = false }) {
    // Form data state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
    });

    // State to track which fields have been touched
    const [touched, setTouched] = useState({
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
        message: false,
    });

    // Validation errors
    const [errors, setErrors] = useState({});

    // Form submission state
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);

    const captchaRef = useRef(null);

    /**
     * Validates the form values
     * @param {Object} values - The form values to validate
     * @returns {Object} - An object containing error messages for invalid fields
     */
    const validate = (values) => {
        const newErrors = {};
        // Match the server's email regex exactly (contact.controller.js). The
        // looser /^\S+@\S+\.\S+$/ accepted multi-@ addresses the server rejects.
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // Name: 2-50 chars, letters, spaces, hyphens, apostrophes only
        const nameRegex = /^[a-zA-Z\s\-']{2,50}$/;
        // Phone: 10+ digits with optional formatting (e.g., 303-555-1234)
        const phoneRegex =
            /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

        if (!values.firstName.trim()) {
            newErrors.firstName = 'First name is required.';
        } else if (!nameRegex.test(values.firstName.trim())) {
            newErrors.firstName =
                'Name must be 2-50 characters (letters, spaces, hyphens, apostrophes only).';
        }

        if (!values.lastName.trim()) {
            newErrors.lastName = 'Last name is required.';
        } else if (!nameRegex.test(values.lastName.trim())) {
            newErrors.lastName =
                'Name must be 2-50 characters (letters, spaces, hyphens, apostrophes only).';
        }

        if (!values.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(values.email)) {
            newErrors.email = 'Please provide a valid email.';
        }

        if (values.phone && !phoneRegex.test(values.phone.trim())) {
            newErrors.phone =
                'Please provide a valid phone number (e.g., 303-555-1234).';
        }

        if (!values.message.trim()) {
            newErrors.message = 'Message is required.';
        }

        return newErrors;
    };

    /**
     * Handles input change and validates
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    /**
     * Handles input blur to mark field as touched
     */
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        setErrors(validate(formData));
    };

    /**
     * Handles form submission
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({
            firstName: true,
            lastName: true,
            email: true,
            phone: true,
            message: true,
        });

        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setSubmitError('');

        try {
            await publicAxios.post('/api/contact', {
                ...formData,
                captchaToken,
            });

            setSubmitSuccess(true);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                message: '',
            });
            setErrors({});
            setValidated(false);
            setTouched({
                firstName: false,
                lastName: false,
                email: false,
                phone: false,
                message: false,
            });

            setTimeout(() => {
                setSubmitSuccess(false);
            }, 5000);
        } catch (error) {
            setSubmitError(
                error.response?.data?.error ||
                    'Failed to send message. Please try again.'
            );
        } finally {
            setLoading(false);
            captchaRef.current?.reset();
            setCaptchaToken(null);
        }
    };

    return (
        <div id="contactus" className={inModal ? 'in-modal' : ''}>
            {!inModal && (
                <SEO
                    title="Contact Us"
                    description="Get in touch with the Sustainability Hub team."
                    breadcrumbs={[
                        { name: 'Home', path: '/' },
                        { name: 'Contact', path: '/contact' },
                    ]}
                />
            )}
            <Container className={inModal ? 'p-0' : 'form-container'}>
                <Form noValidate onSubmit={handleSubmit} className="mx-auto">
                    {/* Header - hidden when in modal since modal has its own */}
                    {!inModal && (
                        <Row className="mb-3 form-header">
                            <h1 className="form-title text-primary">
                                Contact Us
                            </h1>
                            <div className="form-description h6">
                                Have a question? Want to learn more or get
                                involved? Please utilize the form below to get
                                in touch with us!
                            </div>
                        </Row>
                    )}

                    {/* Success Message */}
                    {submitSuccess && (
                        <div
                            className="alert alert-success text-center"
                            role="alert"
                        >
                            Thank you for your message! We&apos;ll get back to
                            you soon.
                        </div>
                    )}

                    {/* Error Message */}
                    {submitError && (
                        <div
                            className="alert alert-danger text-center"
                            role="alert"
                        >
                            {submitError}
                        </div>
                    )}

                    {/* Name Fields */}
                    <Row className="mb-3 form-name">
                        <Form.Group as={Col} md="6" className="mb-3">
                            <InputGroup
                                hasValidation
                                className="input-log-label"
                            >
                                <img src={userIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingFirstName"
                                    label="First Name"
                                >
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        placeholder="Enter your first name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                            touched.firstName &&
                                            !!errors.firstName
                                        }
                                        isValid={
                                            touched.firstName &&
                                            !errors.firstName &&
                                            formData.firstName.trim() !== ''
                                        }
                                        disabled={loading}
                                        aria-describedby={
                                            validated && errors.firstName
                                                ? 'firstName-error'
                                                : undefined
                                        }
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.firstName && (
                                <div
                                    id="firstName-error"
                                    role="alert"
                                    className="invalid-feedback d-block fw-semibold ps-3"
                                >
                                    {errors.firstName}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group as={Col} md="6" className="mb-3">
                            <InputGroup
                                hasValidation
                                className="input-log-label"
                            >
                                <img src={userIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingLastName"
                                    label="Last Name"
                                >
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        placeholder="Enter your last name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                            touched.lastName &&
                                            !!errors.lastName
                                        }
                                        isValid={
                                            touched.lastName &&
                                            !errors.lastName &&
                                            formData.lastName.trim() !== ''
                                        }
                                        disabled={loading}
                                        aria-describedby={
                                            validated && errors.lastName
                                                ? 'lastName-error'
                                                : undefined
                                        }
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.lastName && (
                                <div
                                    id="lastName-error"
                                    role="alert"
                                    className="invalid-feedback d-block fw-semibold ps-3"
                                >
                                    {errors.lastName}
                                </div>
                            )}
                        </Form.Group>
                    </Row>

                    {/* Email Field */}
                    <Row className="mb-3 form-email">
                        <Form.Group as={Col} md="12">
                            <InputGroup
                                hasValidation
                                className="input-log-label"
                            >
                                <img src={emailIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingEmail"
                                    label="Email"
                                >
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        placeholder="name@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                            touched.email && !!errors.email
                                        }
                                        isValid={
                                            touched.email &&
                                            !errors.email &&
                                            formData.email.trim() !== ''
                                        }
                                        disabled={loading}
                                        aria-describedby={
                                            validated && errors.email
                                                ? 'email-error'
                                                : undefined
                                        }
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.email && (
                                <div
                                    id="email-error"
                                    role="alert"
                                    className="invalid-feedback d-block fw-semibold ps-3"
                                >
                                    {errors.email}
                                </div>
                            )}
                        </Form.Group>
                    </Row>

                    {/* Phone Field */}
                    <Row className="mb-3 form-phone">
                        <Form.Group as={Col} md="12">
                            <InputGroup
                                hasValidation
                                className="input-log-label"
                            >
                                <img src={phoneIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingPhone"
                                    label="Phone (Optional)"
                                >
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        placeholder="303-000-0000"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                            touched.phone && !!errors.phone
                                        }
                                        isValid={
                                            touched.phone &&
                                            !errors.phone &&
                                            formData.phone.trim() !== ''
                                        }
                                        disabled={loading}
                                        aria-describedby={
                                            validated && errors.phone
                                                ? 'phone-error'
                                                : undefined
                                        }
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.phone && (
                                <div
                                    id="phone-error"
                                    role="alert"
                                    className="invalid-feedback d-block fw-semibold ps-3"
                                >
                                    {errors.phone}
                                </div>
                            )}
                        </Form.Group>
                    </Row>

                    {/* Message Field */}
                    <Row className="mb-3 form-comment">
                        <Form.Group as={Col} md="12">
                            <Form.Control
                                as="textarea"
                                name="message"
                                aria-label="Message"
                                placeholder="Let us know how we can help you."
                                value={formData.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.message && !!errors.message}
                                isValid={
                                    touched.message &&
                                    !errors.message &&
                                    formData.message.trim() !== ''
                                }
                                disabled={loading}
                                className="message-textarea"
                                aria-describedby={
                                    validated && errors.message
                                        ? 'message-error'
                                        : undefined
                                }
                            />
                            {validated && errors.message && (
                                <div
                                    id="message-error"
                                    role="alert"
                                    className="invalid-feedback d-block fw-semibold"
                                >
                                    {errors.message}
                                </div>
                            )}
                        </Form.Group>
                    </Row>

                    {/* Captcha */}
                    <Row className="mb-4 justify-content-center">
                        <Col xs="auto">
                            <Captcha
                                ref={captchaRef}
                                onVerify={setCaptchaToken}
                                size="normal"
                                theme="light"
                                onExpired={() => setCaptchaToken(null)}
                                onError={() => setCaptchaToken(null)}
                            />
                        </Col>
                    </Row>

                    {/* Submit Button */}
                    <Button
                        className="form-send-btn fw-bold"
                        type="submit"
                        disabled={loading || !captchaToken}
                    >
                        {loading ? 'Sending...' : 'Send'}
                    </Button>
                </Form>
            </Container>
        </div>
    );
}

ContactUs.propTypes = {
    inModal: PropTypes.bool,
};
